import { Injectable } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Subject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { ConfigService } from '../config-service/config.service';

import { AlertHandlerService } from '@finxone-platform/shared/services';
import { WorkflowConfig } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as jsondiffpatch from 'jsondiffpatch';
import { ActiveConfigService } from '../active-config/active-config.service';
import { OrganisationService } from '../organisation.service';

export enum WorkflowConfigChangeStatus {
  FAIL = 'Server failed to apply diff',
  SUCCESS = 'Server successfully applied diff',
}

export class WorkflowConfigDeltaAction {
  static readonly type = '[WorkflowConfig] WorkflowConfigDeltaAction';
  /** `own`: this editor sent the diff and already holds it, so it is not applied again. */
  constructor(
    public diff: jsondiffpatch.Delta,
    public id: string,
    public stack?: string,
    public time?: Date,
    public own = false,
  ) {}
}

/**
 * A `workflowConfigChange` broadcast. `revision` is the draft revision the server's write
 * produced (absent from an older server, or for a diff relayed while no draft was live):
 * stamped changes are applied strictly in revision order.
 */
export type WorkflowConfigChange = {
  type: 'diff' | 'full';
  workflowConfig?: WorkflowConfig;
  diff?: jsondiffpatch.Delta;
  stack?: string;
  id: string;
  revision?: number;
};

/** The answer to `requestEditorResync`: `revision` null when the server holds no live draft. */
type EditorResync = { revision: number | null; workflowConfig?: WorkflowConfig };

/** How long a revision gap may stay open before the editor resyncs. */
export const REVISION_GAP_TIMEOUT_MS = 1000;
const RESYNC_TIMEOUT_MS = 15000;

export class WorkflowConfigFailedToApplyDiffAction {
  static readonly type = '[WorkflowConfig] WorkflowConfigFailedToApplyDiffAction';
  constructor(public payload: { diff?: jsondiffpatch.Delta; id: string }) {}
}

export class WorkflowConfigFullUpdateAction {
  static readonly type = '[WorkflowConfig] WorkflowConfigFullUpdateAction';
  constructor(
    public config: WorkflowConfig,
    public id: string,
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public projectSessionSocket: Socket;
  private baseUrl = '';
  private token: string | null = null;
  public currentlySubscribedProjectSession: string | undefined;

  private enabled = true;

  // Ordering of stamped changes. `revision` is the last one applied (null: no baseline yet);
  // `early` holds changes that arrived ahead of a missing revision; while `syncing`, everything
  // stamped waits in `early` for the state the server is about to send.
  private revision: number | null = null;
  private early = new Map<number, WorkflowConfigChange>();
  private syncing = false;
  /** Bumped by every join/resync, so the answer to a superseded one is ignored. */
  private syncAttempt = 0;
  private gapTimer: ReturnType<typeof setTimeout> | undefined;
  /** Ids of diffs this editor sent that the server has neither echoed nor rejected yet. */
  private ownInFlight = new Set<string>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(
    public configService: ConfigService,
    private activeConfigService: ActiveConfigService,
    private readonly store: Store,
    private readonly alertService: AlertHandlerService,
    private readonly orgService: OrganisationService,
  ) {
    this.configService.getConfig().subscribe((config) => {
      this.enabled = config.multiplayerAppEditorEnabled ?? true;
      this.baseUrl = config['api_endpoints']['provisioner_service'];
    });
  }

  setupSocketProjectSessionConnection() {
    this.token = sessionStorage.getItem('token');

    if (!this.token) {
      // If no token retry in a half second
      setTimeout(() => {
        this.setupSocketProjectSessionConnection();
      }, 500);
    }

    let path = '';
    if (this.baseUrl.includes('localhost')) {
      path = '/editor-update/events';
    } else {
      path = '/api/provisioner/editor-update/events';
    }
    this.projectSessionSocket = io(this.baseUrl, {
      path: path,
      query: {
        bearerToken: this.token,
        orgId: this.orgService.activeOrgId(),
      },
      transports: ['websocket'],
    });
    this.projectSessionSocket.connect();

    // Global events are bound against socket
    this.projectSessionSocket.on('connect_error', () => {
      console.error('Socket IO connection failed');
      this.alertService.showAlertFn('error', 'Failed to connect to server live sessions');
    });
    this.projectSessionSocket.on('connect', function () {
      console.log('Connected to socket io backend');
    });
    this.projectSessionSocket.on('disconnect', function () {
      console.log('Disconnected from socket io backend');
    });
    // The server keeps no connection state across a reconnect (it may land on another replica),
    // so a reconnected socket starts with no rooms: rejoin the session to keep receiving changes,
    // then resync, since whatever was broadcast while disconnected is not replayed.
    this.projectSessionSocket.io.on('reconnect', () => {
      if (this.currentlySubscribedProjectSession) {
        this.joinRoom(this.currentlySubscribedProjectSession);
      }
    });

    this.projectSessionSocket.on('workflowConfigChange', (payload: WorkflowConfigChange) =>
      this.receiveChange(payload),
    );

    this.projectSessionSocket.on(
      'failedToApplyDiff',
      (payload: { diff?: jsondiffpatch.Delta; id: string }) => {
        this.ownInFlight.delete(payload.id);
        console.error(`${WorkflowConfigChangeStatus.FAIL}:`, payload);
        this.alertService.showAlertFn('error', WorkflowConfigChangeStatus.FAIL);
        this.store.dispatch(new WorkflowConfigFailedToApplyDiffAction(payload));
      },
    );
  }

  leaveEditorSession(projectId: string | undefined = this.currentlySubscribedProjectSession) {
    if (this.enabled) {
      try {
        if (projectId && this.projectSessionSocket) {
          this.projectSessionSocket.emit('leaveEditorSession', {
            projectId: projectId,
          });
          // The socket exists only for the session; a code project or the dashboard holds none.
          this.projectSessionSocket.disconnect();
          this.projectSessionSocket = undefined as unknown as Socket;
          this.resetOrdering();
        } else {
          console.error(
            `Could not leave session as either projectId: ${projectId} was undefined or no currentlySubscribedProjectSession ${this.currentlySubscribedProjectSession}`,
          );
        }
      } catch (e) {
        console.error(`Failed to leave editor session for: ${projectId}`);
      }
    }
  }

  joinEditorSession(projectId: string) {
    if (this.enabled) {
      if (this.currentlySubscribedProjectSession) {
        this.leaveEditorSession(this.currentlySubscribedProjectSession);
      }
      if (!this.projectSessionSocket) {
        this.setupSocketProjectSessionConnection();
      }
      this.projectSessionSocket.on(`activeSessions-${projectId}`, (sessionsCount: number) => {
        this.activeConfigService.activeSessionsCount = sessionsCount;
      });
      this.projectSessionSocket.on(
        `activeUsers-${projectId}`,
        (
          sessionUsers: {
            id: string;
            clientId: string;
            firstName: string;
            lastName: string;
            email: string;
          }[],
        ) => {
          this.activeConfigService.activeSessionUsers = sessionUsers;
        },
      );
      this.currentlySubscribedProjectSession = projectId;
      this.joinRoom(projectId);
    }
  }

  /**
   * Joins the project room, then resyncs: the config loaded over REST (or held across a
   * reconnect) carries no revision, and changes made before the join landed were not received.
   * A server that does not ack the join sends no revisions either, and nothing is buffered.
   */
  private joinRoom(projectId: string) {
    const attempt = this.startSync();
    this.projectSessionSocket.emit('joinEditorSession', { projectId }, (ack?: { joined?: boolean }) => {
      if (attempt !== this.syncAttempt || projectId !== this.currentlySubscribedProjectSession) return;
      if (ack?.joined) this.sendResync(projectId, attempt);
      else this.endSync();
    });
  }

  private receiveChange(change: WorkflowConfigChange) {
    if (typeof change.revision !== 'number') {
      this.deliver(change);
      return;
    }
    if (this.revision !== null && change.revision <= this.revision) return; // duplicate or stale
    this.early.set(change.revision, change);
    this.drain();
  }

  /** Applies buffered changes while the next revision is there; a hole left open arms the gap timer. */
  private drain() {
    if (this.syncing) return;
    if (this.revision === null && this.early.size > 0) {
      // No baseline (the server had no live draft): the first change seen starts the sequence.
      this.revision = Math.min(...this.early.keys()) - 1;
    }
    for (const revision of [...this.early.keys()]) {
      if (this.revision !== null && revision <= this.revision) this.early.delete(revision);
    }
    let next: WorkflowConfigChange | undefined;
    while (this.revision !== null && (next = this.early.get(this.revision + 1))) {
      this.early.delete(this.revision + 1);
      this.revision += 1;
      this.deliver(next);
    }
    if (this.early.size === 0) {
      clearTimeout(this.gapTimer);
      this.gapTimer = undefined;
    } else if (!this.gapTimer) {
      this.gapTimer = setTimeout(() => {
        this.gapTimer = undefined;
        this.requestResync();
      }, REVISION_GAP_TIMEOUT_MS);
    }
  }

  private deliver(change: WorkflowConfigChange) {
    if (change.type === 'diff' && change.diff) {
      const own = this.ownInFlight.delete(change.id);
      this.store.dispatch(
        new WorkflowConfigDeltaAction(change.diff, change.id, change.stack, undefined, own),
      );
    }
    if (change.type === 'full' && change.workflowConfig) {
      this.store.dispatch(new WorkflowConfigFullUpdateAction(change.workflowConfig, change.id));
    }
  }

  private requestResync() {
    const projectId = this.currentlySubscribedProjectSession;
    if (this.syncing || !projectId || !this.projectSessionSocket) return;
    this.sendResync(projectId, this.startSync());
  }

  private sendResync(projectId: string, attempt: number) {
    this.projectSessionSocket
      .timeout(RESYNC_TIMEOUT_MS)
      .emit(
        'requestEditorResync',
        { projectId, knownRevision: this.revision },
        (error: Error | null, resync?: EditorResync) => {
          if (attempt !== this.syncAttempt || projectId !== this.currentlySubscribedProjectSession) return;
          if (!error && resync) this.applyResync(resync);
          this.endSync();
        },
      );
  }

  private applyResync(resync: EditorResync) {
    if (resync.workflowConfig && resync.revision !== null) {
      // The server's draft replaces ours. Diffs we sent that it did not include will reach us
      // as later revisions, and must then be applied like anyone else's.
      this.ownInFlight.clear();
      this.revision = resync.revision;
      this.store.dispatch(
        new WorkflowConfigFullUpdateAction(resync.workflowConfig, `resync-${resync.revision}`),
      );
    } else {
      // Already current, or no live draft (we keep our state and take the next change as the baseline).
      this.revision = resync.revision;
    }
  }

  private startSync(): number {
    this.syncing = true;
    clearTimeout(this.gapTimer);
    this.gapTimer = undefined;
    return ++this.syncAttempt;
  }

  private endSync() {
    this.syncing = false;
    this.drain();
  }

  private resetOrdering() {
    clearTimeout(this.gapTimer);
    this.gapTimer = undefined;
    this.revision = null;
    this.early.clear();
    this.syncing = false;
    this.syncAttempt += 1;
    this.ownInFlight.clear();
  }

  distributeWorkflowConfigChange(
    projectId: string,
    diff: jsondiffpatch.Delta,
    id: string,
    stack?: string,
    time?: Date,
  ) {
    if (this.enabled && this.projectSessionSocket) {
      this.ownInFlight.add(id);
      this.projectSessionSocket.emit('distributeWorkflowConfigChange', {
        projectId: projectId,
        diff: diff,
        stack: stack,
        id: id,
        time: time ?? new Date(),
      });
    }
  }
}
